import { WorkflowId } from "@sp-crm/core";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import {
    AdvancedSearchEntityType,
    CustomListKey,
    useCreateWorkflowMutation,
    useGetWorkflowsQuery,
    WorkflowStatus,
} from "generated/graphql";
import { useTaskNouns } from "hooks/use-task-nouns";
import React from "react";
import { Redirect, useRouteMatch } from "react-router-dom";
import { navigate } from "store/actions";
import { useCustomList, useWorkflowEntityTypes } from "store/selectors/bridge";
import { useCurrentUserId, useDefaultRegionVisibility } from "store/selectors/hooks";
import {
    BlueprintContext,
    composeNewWorkflow,
    defaultBlueprint,
} from "./workflow-blueprint";
import { WorkflowCardList } from "./workflow-card-list";
import { WorkflowWizard } from "./workflow-wizard";

export const WorkflowBuilderPage: React.FC = () => {
    const { params } = useRouteMatch<{ workflowId?: WorkflowId }>();
    const workflows = useGetWorkflowsQuery();
    const workflowEntityTypes = useWorkflowEntityTypes();
    const nouns = useTaskNouns();
    const currentUserId = useCurrentUserId();
    const defaultRegionVisibility = useDefaultRegionVisibility();
    const createWorkflowMutation = useCreateWorkflowMutation();
    const { customList: clientStatusCustomList } = useCustomList(
        CustomListKey.ClientStatus,
    );
    const { customList: homeCareLeadStatusCustomList } = useCustomList(
        CustomListKey.HomeCareLeadStatus,
    );
    const [searchText, setSearchText] = React.useState("");
    const [entityTypeFilter, setEntityTypeFilter] = React.useState<
        AdvancedSearchEntityType | "all"
    >("all");
    const [statusFilter, setStatusFilter] = React.useState<WorkflowStatus | "all">("all");

    const handleRefetch = React.useCallback(() => {
        workflows.refetch();
    }, [workflows]);

    const blueprintContext = React.useMemo<BlueprintContext>(
        () => ({
            nouns,
            clientStatusCustomList: clientStatusCustomList ?? null,
            homeCareLeadStatusCustomList: homeCareLeadStatusCustomList ?? null,
        }),
        [nouns, clientStatusCustomList, homeCareLeadStatusCustomList],
    );

    const createBlueprint = React.useMemo(
        () => defaultBlueprint(workflowEntityTypes, blueprintContext),
        [workflowEntityTypes, blueprintContext],
    );

    const createInFlight = React.useRef(false);

    const handleCreate = React.useCallback(async () => {
        if (createInFlight.current || !createBlueprint) {
            return;
        }
        const payload = composeNewWorkflow(createBlueprint, blueprintContext, {
            currentUserId,
            regionVisibility: defaultRegionVisibility,
            existingNames: (workflows.data?.getWorkflows ?? []).map(
                workflow => workflow.name,
            ),
        });
        if (!payload) {
            return;
        }
        createInFlight.current = true;
        try {
            const result = await createWorkflowMutation.mutateAsync({
                workflow: payload,
            });
            await workflows.refetch();
            navigate(`/settings/workflow/${result.createWorkflow.id}`);
        } finally {
            createInFlight.current = false;
        }
    }, [
        createBlueprint,
        blueprintContext,
        currentUserId,
        defaultRegionVisibility,
        createWorkflowMutation,
        workflows,
    ]);

    const canCreate = !!createBlueprint && !createWorkflowMutation.isLoading;

    return (
        <QueryRenderer name="WorkflowBuilderPage" query={workflows}>
            {data => {
                const visibleWorkflows = data.getWorkflows.filter(workflow =>
                    workflowEntityTypes.includes(workflow.attachTo),
                );
                const selectedWorkflow = params?.workflowId
                    ? visibleWorkflows.find(w => w.id === params.workflowId) || null
                    : null;

                if (params?.workflowId && !selectedWorkflow) {
                    return <Redirect to="/settings/workflow" />;
                }

                if (selectedWorkflow) {
                    return (
                        <WorkflowWizard
                            key={selectedWorkflow.id}
                            workflow={selectedWorkflow}
                            workflowSettings={data.getWorkflowSettings}
                            refetch={handleRefetch}
                        />
                    );
                }

                return (
                    <WorkflowCardList
                        workflows={visibleWorkflows}
                        onCreate={handleCreate}
                        createEnabled={canCreate}
                        searchText={searchText}
                        onSearchTextChange={setSearchText}
                        entityTypeFilter={entityTypeFilter}
                        onEntityTypeFilterChange={setEntityTypeFilter}
                        statusFilter={statusFilter}
                        onStatusFilterChange={setStatusFilter}
                    />
                );
            }}
        </QueryRenderer>
    );
};
